import { useState, useEffect } from "react";
import api from "../services/api";
import { getCourses, getTeacherCourses } from "../services/courses";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import Sidebar from "../components/Sidebar";
import {
  Video,
  Calendar as CalendarIcon,
  Trash2,
  Edit3,
  PlayCircle,
  Clock,
  ClipboardCheck
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import ZoomMtgEmbedded from "@zoom/meetingsdk/embedded";

export default function TeacherLiveClasses({ defaultCourseId, isEmbedded }) {
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [scheduleTimes, setScheduleTimes] = useState({});
  const [liveClasses, setLiveClasses] = useState([]);
  const [activeClass, setActiveClass] = useState(null);
  const [client, setClient] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [newTime, setNewTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [isFreeClass, setIsFreeClass] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [liveClassTitle, setLiveClassTitle] = useState("");

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (!token) return;
      const user = jwtDecode(token);

      try {
        const res = await getTeacherCourses();
        const myCourses = res.data;
        setCourses(myCourses);
        if (myCourses.length > 0) {
          const firstCourseId = defaultCourseId ? Number(defaultCourseId) : myCourses[0].id;
          setSelectedCourse(firstCourseId);
        }
        await loadTeacherLiveClasses();
      } catch (err) {
        toast.error("Failed to load courses");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const loadTeacherLiveClasses = async () => {
    try {
      const res = await api.get("/live/teacher");
      setLiveClasses(res.data);
    } catch (err) {
      console.error("Failed to load live classes");
    }
  };

  const handleScheduleClass = async (courseId) => {
    const time = scheduleTimes[courseId];
    if (!time) return toast.error("Select date & time");
    if (!liveClassTitle) return toast.error("Enter a title for the live class");
    if (isFreeClass && !youtubeUrl) return toast.error("Enter YouTube URL for free class");
    try {
      await api.post(`/live/create/${courseId}`, { title: liveClassTitle, scheduled_at: time, is_free: isFreeClass, youtube_url: youtubeUrl });
      toast.success("Live class scheduled!");
      setYoutubeUrl("");
      setIsFreeClass(false);
      setLiveClassTitle("");
      loadTeacherLiveClasses();
    } catch (err) {
      toast.error("Scheduling failed");
    }
  };

  const handleDeleteClass = async (id) => {
    if (!window.confirm("Delete this class?")) return;
    try {
      await api.delete(`/live/${id}`);
      toast.success("Deleted");
      loadTeacherLiveClasses();
    } catch (err) {
      toast.error("Delete failed");
    }
  };

  const handleUpdateClass = async (id) => {
    try {
      await api.put(`/live/${id}`, { scheduled_at: newTime });
      setEditingClass(null);
      toast.success("Updated");
      loadTeacherLiveClasses();
    } catch (err) {
      toast.error("Update failed");
    }
  };

  useEffect(() => {
    if (activeClass && activeClass.meeting_id) {
      // 📡 SKIP ZOOM SDK IF WEBRTC MODE
      const isWebRTC = activeClass.platform === "webrtc" || activeClass.platform === "youtube" || (activeClass.join_url && activeClass.join_url.includes("jit.si"));
      if (isWebRTC) return;

      const startZoom = async () => {
        try {
          const [sigRes, zakRes] = await Promise.all([
            api.get(`/live/signature/${activeClass.meeting_id}?role=1`),
            api.get("/live/zak")
          ]);
          const { signature, sdkKey } = sigRes.data;
          const { zak } = zakRes.data;
          const zoomClient = client || ZoomMtgEmbedded.createClient();
          if (!client) setClient(zoomClient);
          const meetingElement = document.getElementById("meetingSDKElement");
          if (!meetingElement) return;

          await zoomClient.init({ zoomAppRoot: meetingElement, language: "en-US", patchJsMedia: true, leaveOnPageUnload: true });
          await zoomClient.join({ sdkKey, signature, meetingNumber: activeClass.meeting_id, password: activeClass.password, userName: "Teacher", zak });
        } catch (err) {
          toast.error("Failed to start class");
          setActiveClass(null);
        }
      };
      startZoom();
    }
  }, [activeClass, client]);

  if (loading) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <div className="w-10 h-10 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className={isEmbedded ? "w-full" : "min-h-screen bg-slate-50 dark:bg-slate-950 flex"}>
      {!isEmbedded && <Toaster position="top-center" />}
      {!isEmbedded && <Sidebar role="teacher" />}

      <main className={`flex-1 w-full space-y-10 max-w-5xl mx-auto ${isEmbedded ? "" : "lg:ml-64 p-6 lg:p-10"}`}>
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white">Manage Live Classes</h1>
            <p className="text-slate-500 dark:text-slate-400">Schedule your live sessions, manage Zoom meetings, and broadcast to students.</p>
          </div>
        </header>

        {!isEmbedded && (
        <section className="bg-white dark:bg-slate-900 p-6 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm">
          <h3 className="text-lg font-bold mb-4 flex items-center gap-2 text-blue-600">
            <ClipboardCheck size={20} /> Select Course
          </h3>
          <select
            className="w-full max-w-md px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border dark:border-slate-800 rounded-xl font-bold shadow-sm outline-none focus:ring-2 focus:ring-blue-500/20"
            onChange={(e) => setSelectedCourse(e.target.value === "all" ? "all" : Number(e.target.value))}
            value={selectedCourse || ""}
          >
            {courses.length === 0 && <option value="">No courses available</option>}
            <option value="all">-- View All Live Classes --</option>
            {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </section>
        )}

        {selectedCourse && selectedCourse !== "all" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <section className="bg-white dark:bg-slate-900 p-6 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm space-y-6">
              <h3 className="text-lg font-bold flex items-center gap-2 text-red-500">
                <CalendarIcon size={20} /> Schedule Live Class
              </h3>
              <div className="space-y-4">
                <input
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500/20 font-medium"
                  placeholder="Live Class Title (e.g. Q&A Session)"
                  value={liveClassTitle}
                  onChange={(e) => setLiveClassTitle(e.target.value)}
                />
              <input
                type="datetime-local"
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500/20 font-medium"
                onChange={(e) => setScheduleTimes({ ...scheduleTimes, [selectedCourse]: e.target.value })}
              />
              
              <div className="flex items-center gap-2 px-2 py-1">
                <input type="checkbox" id="isFree" className="w-4 h-4 text-red-600 rounded border-gray-300 focus:ring-red-500" checked={isFreeClass} onChange={(e) => setIsFreeClass(e.target.checked)} />
                <label htmlFor="isFree" className="text-sm font-medium dark:text-slate-300">Free Class (YouTube Live)</label>
              </div>
              {isFreeClass && (
                <input
                  type="url"
                  placeholder="YouTube Live URL"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm outline-none focus:ring-2 focus:ring-red-500/20 font-medium"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                />
              )}

              <button
                onClick={() => handleScheduleClass(selectedCourse)}
                className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-extrabold rounded-xl shadow-lg shadow-red-600/20 transition-all flex items-center justify-center gap-2"
              >
                <Video size={18} /> Schedule Class
              </button>
            </div>
          </section>

          <section className="space-y-6">
            <h3 className="text-lg font-bold flex items-center gap-2 dark:text-white px-2">
              <Clock size={20} className="text-amber-500" /> Upcoming Sessions
            </h3>
            <div className="space-y-4">
              {liveClasses.filter(l => selectedCourse === "all" ? true : l.course_id == selectedCourse).length === 0 && (
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 text-center text-slate-400 italic text-sm shadow-sm">
                  No classes scheduled
                </div>
              )}
              {liveClasses.filter(l => selectedCourse === "all" ? true : l.course_id == selectedCourse).map((l) => (
                <motion.div layout key={l.id} className="bg-white dark:bg-slate-900 p-5 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm space-y-4 hover:-translate-y-1 transition-all group">
                  <div className="flex justify-between items-start">
                    <div>
                      <h5 className="font-extrabold text-sm truncate w-48 text-slate-900 dark:text-white">
                        {l.title || courses.find(c => c.id === l.course_id)?.title}
                      </h5>
                      <span className="text-[10px] text-blue-500 font-bold uppercase">{courses.find(c => c.id === l.course_id)?.title}</span>
                      <br/>
                      <span className="text-xs text-slate-500 font-medium">{new Date(l.scheduled_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setEditingClass(l.id); setNewTime(l.scheduled_at.substring(0, 16)); }} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"><Edit3 size={14} /></button>
                      <button onClick={() => handleDeleteClass(l.id)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl text-red-500 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>

                  {editingClass === l.id && (
                    <div className="flex gap-2 animate-fade-in">
                      <input type="datetime-local" className="flex-1 text-xs p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-medium outline-none focus:ring-2 focus:ring-blue-500/20" value={newTime} onChange={e => setNewTime(e.target.value)} />
                      <button onClick={() => handleUpdateClass(l.id)} className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-xl shadow-sm transition-colors">Save</button>
                    </div>
                  )}

                  <button
                    onClick={() => setActiveClass(l)}
                    className="w-full py-3 bg-red-50 dark:bg-red-900/10 hover:bg-red-600 text-red-600 hover:text-white text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    <PlayCircle size={16} /> Start Session
                  </button>
                </motion.div>
              ))}
            </div>
          </section>
          </div>
        )}

        {selectedCourse === "all" && (
          <div className="max-w-3xl mx-auto space-y-6">
            <h3 className="text-lg font-bold flex items-center gap-2 dark:text-white px-2">
              <Clock size={20} className="text-amber-500" /> All Upcoming Sessions
            </h3>
            <div className="space-y-4">
              {liveClasses.length === 0 && (
                <div className="bg-white dark:bg-slate-900 p-8 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 text-center text-slate-400 italic text-sm shadow-sm">
                  No classes scheduled
                </div>
              )}
              {liveClasses.map((l) => (
                <motion.div layout key={l.id} className="bg-white dark:bg-slate-900 p-5 rounded-[1.5rem] border border-slate-100 dark:border-slate-800 shadow-sm space-y-4 hover:-translate-y-1 transition-all group">
                  <div className="flex justify-between items-start">
                    <div>
                      <h5 className="font-extrabold text-sm truncate w-64 text-slate-900 dark:text-white">
                        {l.title || courses.find(c => c.id === l.course_id)?.title}
                      </h5>
                      <span className="text-[10px] text-blue-500 font-bold uppercase">{courses.find(c => c.id === l.course_id)?.title}</span>
                      <br/>
                      <span className="text-xs text-slate-500 font-medium">{new Date(l.scheduled_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => { setEditingClass(l.id); setNewTime(l.scheduled_at.substring(0, 16)); }} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"><Edit3 size={14} /></button>
                      <button onClick={() => handleDeleteClass(l.id)} className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl text-red-500 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>

                  {editingClass === l.id && (
                    <div className="flex gap-2 animate-fade-in">
                      <input type="datetime-local" className="flex-1 text-xs p-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl font-medium outline-none focus:ring-2 focus:ring-blue-500/20" value={newTime} onChange={e => setNewTime(e.target.value)} />
                      <button onClick={() => handleUpdateClass(l.id)} className="px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white text-xs font-bold rounded-xl shadow-sm transition-colors">Save</button>
                    </div>
                  )}

                  <button
                    onClick={() => setActiveClass(l)}
                    className="w-full py-3 bg-red-50 dark:bg-red-900/10 hover:bg-red-600 text-red-600 hover:text-white text-sm font-bold rounded-xl transition-all flex items-center justify-center gap-2"
                  >
                    <PlayCircle size={16} /> Start Session
                  </button>
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </main>

      <AnimatePresence>
        {activeClass && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[100] flex flex-col"
          >
            <div className="p-4 bg-slate-900 flex justify-between items-center text-white border-b border-white/10">
              <span className="font-bold flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                Host Streaming: {courses.find(c => c.id === activeClass.course_id)?.title}
              </span>
              <button onClick={() => setActiveClass(null)} className="px-4 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-bold transition-all shadow-lg">End Broadcast</button>
            </div>
            {
              (activeClass.platform === "webrtc" || (activeClass.join_url && activeClass.join_url.includes("jit.si")))
              ? <iframe 
                  src={activeClass.join_url} 
                  allow="camera; microphone; fullscreen; display-capture; autoplay" 
                  className="flex-1 w-full border-0 bg-slate-900"
                  title="WebRTC Broadcast"
                />
              : activeClass.platform === "youtube"
              ? <iframe
                  src={activeClass.join_url.replace("watch?v=", "embed/")}
                  allow="autoplay; encrypted-media; fullscreen"
                  className="flex-1 w-full border-0 bg-slate-900"
                  title="YouTube Broadcast"
                />
              : <div id="meetingSDKElement" className="flex-1 bg-black" />
            }
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
